import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Star, Trophy, Info } from 'lucide-react';
import { formatUnits } from 'viem';
import BetPreview from './BetPreview';
import usdtLogo from '../assets/logos/usdt.png';
import { useEnvAddresses } from '../hooks/useEnvAddresses';
import { Race } from '../types/race';

import './RaceCard.css';
import dayjs from 'dayjs';
import { formatTokenAmount } from '../utils/formatTokenAmount';
import { calculateRewardMultiplier } from '../utils/calculateRewardMultiplier';

type RaceCardProps = {
  race: Race;
};

const RaceCard = ({ race }: RaceCardProps) => {
  const { name, totalPool, raceStartTime, tokenStats, creatorFeePercent } = race;
  const initialBets = useMemo(
    () =>
      tokenStats.reduce(
        (acc, tokenStat) => {
          acc[tokenStat.token.id] = '';
          return acc;
        },
        {} as { [tokenId: string]: string }
      ),
    [tokenStats]
  );

  const { usdc } = useEnvAddresses();
  const [showTooltip, setShowTooltip] = useState(false);
  const [bets, setBets] = useState<{ [tokenId: string]: string }>(initialBets);
  const [totalBet, setTotalBet] = useState('');
  const [showBetPreview, setShowBetPreview] = useState(false);

  // Add time calculation logic
  const calculateTimeLeft = useCallback(() => {
    const now = dayjs().unix(); // Current time in seconds
    const timeLeftSeconds = Math.max(0, Number(raceStartTime) - now);

    return {
      hours: Math.floor(timeLeftSeconds / 3600),
      minutes: Math.floor((timeLeftSeconds % 3600) / 60),
      seconds: timeLeftSeconds % 60,
    };
  }, [raceStartTime]);

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const handleBetPreviewOpen = () => {
    const totalBet = Object.values(bets).reduce((acc, bet) => acc + (isNaN(Number(bet)) ? 0 : Number(bet)), 0);
    if (totalBet === 0) return;
    setTotalBet(`${totalBet}`);
    setShowBetPreview(true);
  };

  const handleBetPreviewClose = () => {
    setShowBetPreview(false);
  };

  // Update timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, [calculateTimeLeft, raceStartTime]);

  // Helper function to format numbers with leading zeros
  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  return (
    <div className={`race-card watch-card relative`}>
      <div className="card-header">
        <div className="title-section">
          <div className="title-status">
            <h2>{name}</h2>
          </div>
          <div className="flex gap-3">
            <div className={`stats-buttonD action-buttonD`}>
              <Star size={20} />
            </div>
          </div>
        </div>
      </div>

      <div className="info-section">
        <div className="flex">
          <div className="prize-pool flex-grow">
            <div className="prize-pool flex-grow">
              <span className="prize-pool-label">Total Staking Reward Pool</span>
              <span className="amount">${formatTokenAmount(totalPool, usdc.decimals, 'usdc')}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs">Race Creator Edge</span>
              <span className="text-xs">
                ${formatTokenAmount((creatorFeePercent * totalPool) / BigInt(1e18), usdc.decimals, 'usdc')}
              </span>
            </div>
          </div>
          <div className="timer-section !items-start !gap-1">
            <div className="timer-label">Starts in</div>
            <div className="horizontal-timer">
              <div className="time-unit">
                <span className="time-value">{formatNumber(timeLeft.hours)}</span>
                <span className="time-label">Hours</span>
              </div>
              <div className="time-unit">
                <span className="time-value">{formatNumber(timeLeft.minutes)}</span>
                <span className="time-label">Minutes</span>
              </div>
              <div className="time-unit">
                <span className="time-value">{formatNumber(timeLeft.seconds)}</span>
                <span className="time-label">Seconds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="divider my-4"></div>
      <div className="relative flex min-h-72 flex-col justify-center overflow-hidden">
        <div className="relative flex flex-col gap-6">
          <div className="absolute top-14">Potential Staking Reward Multiplier</div>
          <div className="absolute bottom-11">Race Outcome Based Potential Staking Reward</div>
          <div className="watch-tokens-grid text-xs">
            {tokenStats.map((tokenStat, idx) => {
              const totalBet = Object.values(bets).reduce(
                (acc, bet) => acc + (isNaN(Number(bet)) ? 0 : Number(bet)),
                0
              );
              const betAmount = bets[tokenStat.token.id];
              const poolAmount = Number(
                formatUnits((totalPool * (BigInt(1e18) - creatorFeePercent)) / BigInt(1e18), usdc.decimals)
              );
              const tokenAmount = Number(formatUnits(tokenStat.totalBetsAmount, usdc.decimals));
              const ratio = calculateRewardMultiplier(poolAmount + totalBet, tokenAmount + Number(betAmount));

              // Calculate potential reward based on ratio
              const [numerator, denominator] = ratio.split(':').map(Number);
              const rewardMultiplier = denominator > 0 ? numerator / denominator : 0;
              const potentialReward = Number(betAmount) * (rewardMultiplier - 1);

              return (
                <div key={tokenStat.token.id} className="token-itemc !gap-3">
                  <div className="flex cursor-pointer flex-row items-center gap-2 pb-8">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-white">
                      <img src={tokenStat.token.marketData?.image} alt={`${tokenStat.token.symbol}`} />
                    </div>
                    <div className="text-sm">{tokenStat.token.symbol}</div>
                  </div>
                  <div className="token-odds !mb-0">{ratio}</div>
                  <span className="h-6 text-base leading-6">{idx === 0 ? '$s at Stake' : ' '}</span>
                  <div className="-mt-2 flex items-center gap-1">
                    <div className="token-pool">
                      {formatTokenAmount(tokenStat.totalBetsAmount, usdc.decimals, 'usdc')}
                    </div>
                    <img src={usdtLogo} alt="USDT" className="h-5 w-5" />
                  </div>
                  <div className="relative flex w-full items-center gap-1 rounded-lg border border-gray-800 bg-black p-2">
                    <div
                      className="text-xs text-white/50"
                      onClick={() => {
                        document.getElementById(`stake-${tokenStat.token.id}`)?.focus();
                      }}
                    >
                      Stake
                    </div>
                    <input
                      id={`stake-${tokenStat.token.id}`}
                      value={betAmount}
                      onChange={e => {
                        const value = e.target.value;
                        // Only allow numbers and decimal points
                        if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
                          setBets({ ...bets, [tokenStat.token.id]: value });
                        }
                      }}
                      type="text"
                      className="w-full border-none bg-black p-0 text-right text-sm leading-4 text-white outline-none"
                    />
                    <div className="text-center text-xs text-white/50">$</div>
                  </div>
                  <div className="mt-8 flex w-full items-center gap-1 rounded-lg border border-gray-800 bg-black p-2">
                    <input
                      disabled
                      value={Number(betAmount) > 0 ? `${betAmount} + ${potentialReward.toFixed(2)}` : '-'}
                      placeholder="-"
                      type="text"
                      className="w-full border-none bg-black !p-0 text-right text-sm leading-4 text-white outline-none"
                    />
                    <div className="text-center text-xs text-white/50">$</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="divider mt-4"></div>

      <div className="mt-4 flex">
        <div className="flex flex-grow flex-col gap-2">
          <div className="race-type">
            <Trophy size={16} />
            <span className="text-sm">Handicap: {race.handicapEnabled ? 'YES' : 'NO'} </span>
            <div className="relative">
              <Info
                size={16}
                className="info-icon"
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              />
              {showTooltip && (
                <div className="tooltip">
                  <div>
                    {`{ Percentage return / 30 day volatility } x
                  `}
                  </div>

                  <div>
                    {`
                  Ln { ( FDV of token i / min FDV of race participants ) + 1 }`}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="watch-card-purple-circle"></div>
      <button
        className="watch-button relative z-10 !font-bold"
        aria-label="Watch live race"
        onClick={handleBetPreviewOpen}
      >
        Stake
      </button>

      <BetPreview race={race} bets={bets} totalBet={totalBet} show={showBetPreview} onClose={handleBetPreviewClose} />
    </div>
  );
};

export default RaceCard;
